import type { MenuItemLoginOrMyProfileViewModel } from 'autogen/swagger/all';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useLoginLink from 'hooks/user/useLoginLink';
import loadHtmlModalFunc from 'modules/modal/helpers';
import Default from 'modules/modal/layouts';
import { bindComponents } from 'modules/modalLink';
import { GA4Tracking } from 'modules/tracking/GA4';
import type { DefaultMenuItemProps } from '../types';
import DefaultMenuItem from './DefaultMenuItem';

type LoginOrMyProfileMenuItemProps = {
    model: MenuItemLoginOrMyProfileViewModel;
    defaultProps: DefaultMenuItemProps;
};

export default function LoginOrMyProfileMenuItem({
    model: { myProfileLinkAction, textWhenLoggedIn, textWhenNotLoggedIn },
    defaultProps: { iconUrl, styling, id },
    ...restProps
}: LoginOrMyProfileMenuItemProps) {
    const isAuthenticated = useIsAuthenticated();
    const { loginLinkAction, loginModal } = useLoginLink();

    if (!loginLinkAction && loginModal) {
        return (
            <DefaultMenuItem.Button
                styling={styling}
                iconUrl={iconUrl}
                onClick={async () => {
                    const htmlModal = await loadHtmlModalFunc();
                    const modal = htmlModal({ layout: { component: (props) => <Default {...props} /> } });
                    modal({ url: loginModal.linkAction.url, onAfterContent: bindComponents });
                }}
                {...restProps}
            >
                {textWhenNotLoggedIn}
            </DefaultMenuItem.Button>
        );
    }

    if (!isAuthenticated && loginLinkAction)
        return (
            <DefaultMenuItem.Anchor
                anchorProps={{ action: loginLinkAction, title: textWhenNotLoggedIn }}
                styling={styling}
                iconUrl={iconUrl}
                onClick={() => {
                    GA4Tracking({
                        eventName: 'open_sso_modal',
                        context: 'menu',
                        category: 'login'
                    });
                }}
                {...restProps}
            >
                {textWhenNotLoggedIn}
            </DefaultMenuItem.Anchor>
        );

    return (
        <DefaultMenuItem.Anchor
            anchorProps={{ action: myProfileLinkAction, title: textWhenLoggedIn }}
            styling={styling}
            iconUrl={iconUrl}
            onClick={() => {
                GA4Tracking({
                    eventName: 'link_click',
                    context: 'menu',
                    category: 'navigation',
                    eventParams: [
                        { param_name: 'link_type', param_value: 'internal' },
                        { param_name: 'list_name', param_value: textWhenLoggedIn },
                        { param_name: 'id', param_value: id }
                    ]
                });
            }}
            {...restProps}
        >
            {textWhenLoggedIn}
        </DefaultMenuItem.Anchor>
    );
}
